import * as React from "react";
import {autorun, reaction} from "mobx";
import {observer} from "mobx-react";

import {CheckboxProps, Message} from "semantic-ui-react";

import "./layer-list.scss";

import EsriLayerListViewModel from "@arcgis/core/widgets/LayerList/LayerListViewModel";

import {LayerListConfiguration} from "../../config/layer-list-configuration";

import {SyntheticEvent} from "react";
import {LayerListList} from "./layer-list-list/layer-list-list";
import {SidebarControlsConfiguration} from "../../../config/sidebar-configuration";
import {Legend as CustomLegend} from "../../../components/app/sidebar/legend/legend";
import {Legend as CoreLegend} from "../../../core/components/legend/legend";
import {TopicLayersConfiguration} from "../../../config/topic-configuration";
import {PathUtils} from "../../../utils/path-utils";
import _ from "lodash";
import WatchHandle = __esri.WatchHandle;
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import * as arcade from "@arcgis/core/arcade";
import {ListItemConfiguration} from "../../config/list-item-configuration";
import {MessageConfiguration} from "../../config/message-configuration";
import ListItem from "@arcgis/core/widgets/LayerList/ListItem";
import GroupLayer from "@arcgis/core/layers/GroupLayer";
import Collection from "@arcgis/core/core/Collection";
import {Icon} from "../icon/icon";
import Sublayer from "@arcgis/core/layers/support/Sublayer";
import {IAppStore, withStore} from "../../stores/abstract-app-store";

export interface LayerListProps {
    config: LayerListConfiguration;
    controlsConfig: SidebarControlsConfiguration;
    hideList: boolean; // If list is hidden (only shows legend and messages)
    onLoad?: (layerListViewModel: EsriLayerListViewModel) => any;
    layers?: TopicLayersConfiguration;
    small?: boolean;
    store?: IAppStore; // this is injected by withStore
}
// Layer um index und uid ergänzen. Der Index wird zum Sortierung verwendet
interface ExtendedLayer extends __esri.Layer {
    index: number;
    limitLevel?: number;
    uid: string;
}
interface ExtendedSublayer extends Sublayer {
    index: number;
    children: number[];
    limitLevel?: number;
    uid: string;
    visibilityMode: string;
}

@observer
class LayerListBase extends React.Component<LayerListProps, any> {
    autorunDisposers: any[] = [];
    watchHandles: WatchHandle[] = [];
    widgetKey = "layer-list";
    itemsConfig: ListItemConfiguration[] = [];
    itemVisibility = new Map<string, boolean>();
    operationalItems: Collection<ListItem>;
    groupOpenStatusSet: string[] = [];
    layerIds: string[] = []; // Layer IDs für Debug Konfiguration
    groupPromises: Array<Promise<any>> = [];
    nextGroupId = 1000;
    constructor(props: LayerListProps) {
        super(props);

        this.state = {
            postMessages: Element,
            preMessages: Element,
            viewModel: null
        };
        if (typeof this.props.small === "boolean" && this.props.small) {
            this.widgetKey += "-small";
        }
        this.reformatListItemWhenCreated = this.reformatListItemWhenCreated.bind(this);
        this.handleListItemClick = this.handleListItemClick.bind(this);
    }

    /**
     * Remember the state of the list items, so changes can trigger re renderings on child components
     *
     * @param event
     * @param eventData
     * @param listItem
     */
    handleListItemClick(event: SyntheticEvent, eventData: CheckboxProps, listItem: __esri.ListItem): void {
        event.preventDefault();

        const {store} = this.props;

        const visible = !!eventData["checked"];
        const allItems = this.operationalItems.flatten((ite: __esri.ListItem) =>
            ite.children
        );
        const oi = allItems.find((item: __esri.ListItem) =>
            item.layer.id === listItem.layer.id
            || (item.layer as ExtendedLayer).uid === (listItem.layer as ExtendedLayer).uid
        );
        if (!oi) {
            return;
        }
        const extendedSubLayer = oi.parent?.layer as unknown as ExtendedSublayer;

        // Nach Parent Layer überprüfen und für nicht GroupLayer visibilityMode implementieren
        if(oi.parent?.layer && oi.parent?.layer.type!=="group") {
            const parentVisibilityMode = extendedSubLayer.visibilityMode;
            if (parentVisibilityMode === "exclusive") {
                // Im visibilityMode exclusive zuerst alle "children" der Gruppe auf invisible setzten
                if (visible) {
                    oi.parent.children.forEach((i) => {
                        if((oi.layer as ExtendedLayer).uid !== (i.layer as ExtendedLayer).uid) {
                            i.layer.visible = false;
                            // Allfällige dazugehörende "children" auf invisible setzten
                            i.children.flatten((ite: __esri.ListItem) => ite.children).forEach((subI)=> {
                                this.itemVisibility.set((subI.layer as ExtendedLayer).uid, subI.layer.visible);
                                subI.layer.visible = false;
                            });
                        }
                    });
                }
            } else if (parentVisibilityMode === "inherited") {
                // Im visibilityMode inherited "parent" und alle "children" der Gruppe auf den selben Wert setzen
                oi.parent.layer.visible = visible;
                oi.parent.children.forEach((i) => {
                    if (!i.hidden) {
                        i.layer.visible = visible;
                    }
                });
            }
            if (extendedSubLayer.visibilityMode === "inherited") {
                // Im visibilityMode inherited alle "children" der Gruppe auf den selben Wert setzten
                oi.children.forEach((i) => {
                    if (!i.hidden) {
                        i.layer.visible = visible;
                        this.itemVisibility.set((i.layer as ExtendedLayer).uid, visible);
                    }
                });
            }
        }
        this.itemVisibility.set((oi.layer as ExtendedLayer).uid, visible);
        if (visible && extendedSubLayer?.visibilityMode === "exclusive" && oi.children.length > 0) {
            // Sicherstellen, dass im exclusive Modus jeweils nur ein "Child" aktive ist
            let visibleChildren=0;
            oi.children.forEach((i) => {
                if(typeof this.itemVisibility.get((i.layer as ExtendedLayer).uid) === "boolean"
                    && this.itemVisibility.get((i.layer as ExtendedLayer).uid)) {
                    visibleChildren++;
                }
            });
            if(visibleChildren!==1) {
                oi.children.forEach((i, index) => {
                    this.itemVisibility.set((i.layer as ExtendedLayer).uid, index===0);
                });
            }
        }
        oi.children.flatten((ite: __esri.ListItem) => ite.children).forEach((i)=>{
            if (!i.hidden) {
                if (!visible) {
                    this.itemVisibility.set((i.layer as ExtendedLayer).uid, i.layer.visible);
                    i.layer.visible = visible;
                } else if (typeof this.itemVisibility.get((i.layer as ExtendedLayer).uid) === "boolean") {
                    i.layer.visible = this.itemVisibility.get((i.layer as ExtendedLayer).uid) as boolean;
                } else {
                    i.layer.visible = visible;
                }
            }
        });
        oi.visible = visible;
        oi.layer.visible = visible;
        // übergeordnete Layer auf sichtbar setzen
        if(visible && oi.parent) {
            let p = oi.parent;
            while (p) {
                if (p.layer.visible !== visible) {
                    p.layer.visible = visible;
                    p.visible = visible;
                }
                p = p.parent;
            }
        }
        const operationalItemsArray = this.operationalItems.flatten((ite: __esri.ListItem) =>
            ite.children
        ).toArray();
        store?.LayerListStore.setOperationalItemsArray(operationalItemsArray);
    }
    handleLayerChange(layers?: TopicLayersConfiguration): void {
        const {config, store} = this.props;
        if (!store) {
            return;
        }
        if(typeof layers === "undefined" ||
            !this.operationalItems ||
            !store.LayerListStore.operationalItemsArray) {
            return;
        }
        const allItems = this.operationalItems.flatten((ite: __esri.ListItem) =>
            ite.children
        );
        allItems.forEach((item: __esri.ListItem) => {
            // Layer aus CompareWidget ignorieren
            if((item.layer as any)?.metauid !== "[compare]" && item.layer.title !== "[compare]"){
                this.hideLayerItem(false, item);
            }
        });
        // TODO: allItems nur einmal loopen!
        if(layers.hide && Array.isArray(layers.hide)) {
            // single layers
            allItems.forEach((item: __esri.ListItem) => {
                const url = PathUtils.getLayerPath(item.layer);
                if (typeof url === "string") {
                    const metaId = PathUtils.getMetaId(url);
                    const layerId = PathUtils.getLayerId(url);
                    if (typeof layerId === "number") {
                        if (layers.hide.some((layer) => layer.id === metaId
                            && (layer.layerId === layerId || (layer.layerIds && layer.layerIds.includes(layerId))))) {
                            this.hideLayerItem(true, item);
                        }
                    }
                }
            });
            // group layers
            if(Array.isArray(config.groups)){
                config.groups.forEach((itemConfig: ListItemConfiguration) => {
                    const groupId = store?.uiStore.sublayerMap.get(itemConfig.layer.id as string);
                    const groupItem = allItems.find((item: __esri.ListItem) =>
                        (item.layer as ExtendedLayer).uid === groupId
                    );
                    if(groupItem && layers.hide?.some((o)=> o.id === itemConfig.layer.id)) {
                        this.hideLayerItem(true, groupItem);
                    }
                });
            }
        }
        if(layers.visible && layers.visible.length>0) {
            allItems.forEach((item: __esri.ListItem) => {
                const url = PathUtils.getLayerPath(item.layer);
                if (typeof url === "string") {
                    // Layer aus CompareWidget ignorieren
                    if((item.layer as any)?.metauid === "[compare]" || item.layer.title === "[compare]"){
                        return;
                    }
                    const metaId = PathUtils.getMetaId(url);
                    const layerId = PathUtils.getLayerId(url);
                    if (layers.ignore?.some((layer) => layer.id === metaId
                        && (typeof layerId !== "number"
                            || (layer.layerId === layerId || (layer.layerIds && layer.layerIds.includes(layerId)))))) {
                        if(!store?.mapStore.activeLayerMetauids){
                            const layerConfig = config.items.find((l)=>l.layer.id===metaId);
                            if(layerConfig){
                                item.visible = !!layerConfig.layer?.visible;
                                item.layer.visible = item.visible;
                            }
                        } else {
                            item.visible = store.mapStore.activeLayerMetauids.includes(metaId);
                            item.layer.visible = item.visible;
                        }
                        return;
                    } else if (layers.visible.some((layer) => layer.id === metaId
                        && (typeof layerId !== "number"
                            || (layer.layerId === layerId || (layer.layerIds && layer.layerIds.includes(layerId)))))) {
                        item.visible = true;
                        item.layer.visible = true;
                        let p = item.parent;
                        while (p) {
                            if (!p.layer.visible) {
                                p.layer.visible = true;
                                p.visible = true;
                            }
                            p = p.parent;
                        }
                    } else {
                        item.visible = false;
                        item.layer.visible = false;
                    }
                }
            });
        }
        if(layers.open && Array.isArray(layers.open)) {
            if(Array.isArray(config.groups)){
                config.groups.forEach((itemConfig: ListItemConfiguration) => {
                    const groupId = store.uiStore.sublayerMap.get(itemConfig.layer.id as string);
                    const groupItem = allItems.find((item: __esri.ListItem) =>
                        (item.layer as ExtendedLayer).uid === groupId
                    );
                    if(groupItem) {
                        groupItem.open = layers.open?.some((o)=> o.id === itemConfig.layer.id) as boolean;
                    }
                });
            }
        }
        this.reorder();
    }

    updateMessages(){
        const {controlsConfig} = this.props;
        const preMessages = this.prepareMessagesContent(controlsConfig.layerList.preContent);
        Promise.all(preMessages).then((msgs) => {
            this.setState( {preMessages: msgs.filter((m) => m)});
        });
        const messages = this.prepareMessagesContent(controlsConfig.layerList.postContent);
        Promise.all(messages).then((msgs) => {
            this.setState( {postMessages: msgs.filter((m) => m)});
        });
    }
    prepareMessagesContent(messagesConfig?: MessageConfiguration[]): Array<Promise<Element | undefined>> {
        const {store} = this.props;
        const messagePromises: Array<Promise<Element | undefined>> = [];
        if (messagesConfig && Array.isArray(messagesConfig)) {
            const profile: __esri.Profile = {
                variables: [{
                    name: "$subtopicId",
                    type: "text"
                }, {
                    name: "$activeLayers",
                    type: "array",
                    elementType: {
                        type: "text"
                    }
                }, {
                    name: "$activeLayersMeta",
                    type: "array",
                    elementType: {
                        type: "text"
                    }
                }]
            };
            messagesConfig.forEach((message, index) => {
                if (typeof message.condition === "undefined") {
                    messagePromises.push(new Promise((resolve) => {
                        resolve(this.prepareMessageContent(message, index));
                    }));
                } else {
                    if(store?.mapStore.activeLayerMetauids) {
                        const activeLayers: string[] = [];
                        store?.mapStore.activeLayers.forEach((layerNumbers: number[], layerMeta) => {
                            layerNumbers?.forEach((lNo: number)=>{
                                activeLayers.push(`${layerMeta}-${lNo.toString()}`);
                            });
                        });
                        const arcadeExecutor = arcade.createArcadeExecutor(message.condition, profile);
                        messagePromises.push(new Promise((resolve) => {
                            arcadeExecutor.then((ae) => {
                                const condition = ae.execute({
                                    $activeLayers: activeLayers,
                                    $activeLayersMeta: store?.mapStore.activeLayerMetauids,
                                    $subtopicId: (store?.appStore.subtopicId? store?.appStore.subtopicId: "")
                                });
                                let m: JSX.Element | undefined;
                                if (condition) {
                                    m = this.prepareMessageContent(message, index);
                                }
                                resolve(m);
                            });
                        }));
                    }
                }
            });
        }
        return messagePromises;
    }
    prepareMessageContent(messageConfig: MessageConfiguration, index: number): JSX.Element {
        let message: JSX.Element;
        // quick and dirty!
        const messageContent = <p
            dangerouslySetInnerHTML={{__html: messageConfig.content}}/>;
        if (messageConfig.class === "blank"){
            message = <div
                key={index}
                className={messageConfig.class ? messageConfig.class : undefined}
            >{messageContent}</div>;
        } else {
            message = <div className={"geo-message-wrapper"} key={index}><Message
                attached="bottom"
                className={messageConfig.class ? messageConfig.class : undefined}
                content={messageContent}
            /></div>;
        }
        return message;
    }

    render(): JSX.Element | null {
        const {config, controlsConfig, hideList, small, store} = this.props;
        if (store?.LayerListStore.isLoading === null || store?.LayerListStore.isLoading) {
            return <div className={"geo-loading-wrapper"}><Icon name={"recycle"} className={"geo-rotating"} /></div>;
        }
        if (!store?.LayerListStore.viewModel) {
            return (
                <Message negative>
                    <Message.Header>Tut uns leid, der Karteninhalt konnte nicht ausgelesen werden.</Message.Header>
                    <p>Fehler beim Laden des ViewModels.</p>
                </Message>
            );
        }
        const {postMessages, preMessages} = this.state;
        const {hasGroups} = store?.LayerListStore;

        if (!store.LayerListStore.operationalItemsArray) {
            return null;
        }

        let legend;
        if (controlsConfig.legend.position === "toc") {
            if (controlsConfig.legend.useEsri && controlsConfig.legend.esriConfig) {
                legend = (<CoreLegend
                    uiStore={store?.uiStore}
                    view={store?.mapStore.view}
                    config={controlsConfig.legend.esriConfig}
                    controlsConfig={controlsConfig}
                    appStore={store?.appStore}
                    mapStore={store?.mapStore}
                    small={!!small}/>);
            } else {
                legend = (<CustomLegend
                    appStore={store?.appStore}
                    uiStore={store?.uiStore}
                    config={controlsConfig.legend} />);
            }
        }
        const isRoot = (controlsConfig.layerList.startLevel === 0);
        return (
            <div>
                <div className={"geo-layer-list"+(hasGroups? " geo-list-with-groups": "")}>
                    {preMessages}
                    {this.operationalItems?.length > 0 && !hideList && !config.hideList &&
                        <LayerListList
                            listItems={this.operationalItems.toArray()}
                            isRoot={isRoot}
                            level={0}
                            onListItemClick={this.handleListItemClick}
                            config={config}
                            small={small as boolean}
                            controlsConfig={controlsConfig} />
                    }
                    {legend}
                    {postMessages}
                </div>
                <div className="geo-layer-list-footer"/>
            </div>
        );
    }

    componentDidMount(): void {
        const {config, onLoad, store} = this.props;

        // index in items ergänzen, falls keiner vorhanden ist.
        this.itemsConfig = config.items.map((item, index) => {
            if(!item.hasOwnProperty("index")) {
                item.index = index + 1;
            }
            return item;
        });

        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((reaction: any) => {
            if (!store) {
                return;
            }

            if (store?.uiStore.widgets.has(this.widgetKey)) {
                reaction.dispose(); // stop autorun
                return;
            }

            const modelViewInStore = !!store?.LayerListStore.viewModel;
            const viewModel =
                modelViewInStore?
                    store?.LayerListStore.viewModel :
                    this.loadViewModel(store?.mapStore.view, config);

            if (!viewModel) {
                return;
            }

            if(!modelViewInStore) {
                store?.LayerListStore.setViewModel(viewModel);
            }
            this.operationalItems = viewModel.operationalItems;
            const operationalItemsArray = this.operationalItems.flatten((ite: __esri.ListItem) =>
                ite.children
            ).toArray();
            store?.LayerListStore.setOperationalItemsArray(operationalItemsArray);

            if (viewModel.state !== "ready") {
                store?.LayerListStore.setIsLoading(true);
            }

            if(!modelViewInStore || viewModel.state !== "ready") {
                this.addWatchForEsriLayerListReady(viewModel, store);
            }
            if (onLoad) {
                // call the hook
                onLoad(viewModel);
            }
            store?.uiStore.addWidget(this.widgetKey, this);
            reaction.dispose();
        }));
        this.updateMessages();
        this.autorunDisposers.push(reaction(() => store?.mapStore.activeLayerMetauids, () => {
            this.updateMessages();
        }));
        this.autorunDisposers.push(reaction(() => store?.appStore.subtopic?.revision, () => {
            if(!store?.LayerListStore.isLoading && store?.appStore.subtopic?.layers) {
                this.handleLayerChange(store?.appStore.subtopic.layers);
            }
        }));
    }

    componentWillUnmount(): void {
        const {store} = this.props;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        this.watchHandles.forEach((handle: WatchHandle) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        store?.uiStore.removeWidget(this.widgetKey);
    }
    componentDidUpdate(prevProps: any) {
        const {layers} = this.props;
        if (!_.isEqual(prevProps.layers, layers)){
            this.handleLayerChange(layers);
        }
    }

    private addWatchForEsriLayerListReady(viewModel: __esri.LayerListViewModel, store: IAppStore) {
        this.watchHandles.push(reactiveUtils.when(
            () => viewModel.state === "ready" && viewModel.operationalItems.length > 0,
            () => {
                this.loadGroups(viewModel.operationalItems);
                Promise.all(this.groupPromises).then(() => {
                    store?.LayerListStore.setIsLoading(false);
                    store?.mapStore.setTocReady();
                    this.addItemsToGroup(viewModel.operationalItems);
                    this.handleLayerChange(store?.appStore.subtopic?.layers);
                    this.watchHandles.push(viewModel.watch("operationalItems.length",
                        (nValue, oValue, pName: string, targetView: EsriLayerListViewModel) => {
                            const {operationalItems} = targetView;
                            this.operationalItems = operationalItems;
                            store?.LayerListStore.setIsLoading(false);
                            const operationalItemsArray = operationalItems.flatten((ite: __esri.ListItem) =>
                                ite.children
                            ).toArray();
                            store.LayerListStore.setOperationalItemsArray(operationalItemsArray);

                            // watch for visibility changes
                            this.operationalItems.forEach((listItem) => {
                                this.watchHandles.push(listItem.watch("layerView",
                                    (newValue: any, oldValue: any, propertyName: string,
                                     target: __esri.ListItem) => {
                                        target.watch("visible",
                                            () => {
                                                store?.LayerListStore.setIsLoading(false);
                                            });
                                    })
                                );
                            });

                            this.addItemsToGroup(operationalItems);
                        })
                    );
                });
            },
            {initial: true, once: true}
        ));
    }
    private loadViewModel(view: __esri.MapView | __esri.SceneView | undefined,
                          layerListConfig: LayerListConfiguration): EsriLayerListViewModel | null {

        if (!view) {
            return null;
        }

        const config = layerListConfig as __esri.LayerListProperties;
        const props = {
            view,
            listItemCreatedFunction: this.reformatListItemWhenCreated,
            ...config
        };
        return new EsriLayerListViewModel(props);
    }

    /**
     * Add custom groups
     */
    private loadGroups(operationalItems: Collection<ListItem>): void {
        // add groups
        const {config, store} = this.props;
        const {groups} = config;
        if(!groups || store?.LayerListStore.processing) {
            return;
        }
        store?.LayerListStore.setProcessing(true);
        if(config.rebuild && operationalItems.getItemAt(0).layer.type === "map-image"){
            const mapImageLayer = operationalItems.getItemAt(0).layer as __esri.MapImageLayer;
            this.addGroups(mapImageLayer, this.buildHierarchy(groups));
        } else {
            groups.forEach((groupConfig: ListItemConfiguration) => {
                if (!store?.mapStore.view?.map.layers.some(
                    (l: __esri.Layer) => l.id === groupConfig.layer.id)) {
                    if (groupConfig.hasOwnProperty("title") && !groupConfig.layer.hasOwnProperty("title")) {
                        groupConfig.layer.title = groupConfig.title;
                    }
                    // create group layer
                    const groupLayer = new GroupLayer(groupConfig.layer);
                    // add group layer to the map
                    store?.mapStore.view?.map.layers.add(groupLayer, groupConfig.index || 0);
                    // register on mobx store to access it more easily
                    store?.mapStore.addGroupLayer(groupLayer.id, groupLayer);
                    // set reference to group layer on layer item
                    groupConfig.layer = groupLayer;
                    // create list item
                    let groupListItem;
                    try {
                        groupListItem = new ListItem(groupConfig);
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }

                    // add list item to the layer list
                    if(groupListItem) {
                        operationalItems.add(groupListItem);
                    }
                    if (groupConfig.layer.id) {
                        store?.uiStore.sublayerMap.set(groupConfig.layer.id, (groupLayer as ExtendedLayer).uid);
                    }

                    // parent layer?
                    if (groupConfig.hasOwnProperty("parent")) {
                        this.addToParent(groupConfig, groupListItem, operationalItems);
                    }
                }
            });
        }
    }
    private addGroups(mapImageLayer: __esri.MapImageLayer | __esri.Sublayer, groups: any[]): void {
        const {store} = this.props;
        groups.forEach((l, index)=>{
            const copy = _.cloneDeep(l.layer);
            copy.sublayers = [];
            copy.title = l.title;
            copy.legendEnabled = false;
            // Nicht benötigte respektive problematische Attribute entfernen
            delete copy.id;
            // Hohe nummerische ID vergeben, damit diese nicht mit einer ID im Map-Service kollidiert
            copy.id = this.nextGroupId;
            this.nextGroupId++;
            const groupLayer = new Sublayer(copy) as ExtendedSublayer;
            if(l.hasOwnProperty("index")) {
                groupLayer.index = l.index;
            } else {
                groupLayer.index = index;
            }
            if(l.hasOwnProperty("limitLevel")) {
                groupLayer.limitLevel = l.limitLevel;
            }
            if(l.layer.hasOwnProperty("visibilityMode")) {
                groupLayer.visibilityMode = l.layer.visibilityMode;
            }
            mapImageLayer.sublayers.add(groupLayer, l.index);
            this.groupPromises.push(groupLayer.load());
            groupLayer.children = [];
            store?.uiStore.sublayerMap.set(l.layer.id, groupLayer.uid);
            if(l.sublayers) {
                this.addGroups(groupLayer, l.sublayers);
            }
        });
    }
    private buildHierarchy(groups: ListItemConfiguration[]): [] {

        const sublayers = {};
        const roots: [] = [];

        // find the top level nodes and hash the children based on parent
        groups.forEach((groupConfig: ListItemConfiguration) => {
            const p = groupConfig.parent;
            (groupConfig as any).sublayers = [];
            let target: any;
            if (p && p.layer.id) {
                target = (sublayers[p.layer.id] || (sublayers[p.layer.id] = []));
            } else {
                target = roots;
            }
            target.push(groupConfig);
        });

        // function to recursively build the tree
        const findChildren: any = (parent: any) => {
            if (sublayers[parent.layer.id]) {
                parent.sublayers = sublayers[parent.layer.id];
                for (let i = 0, len = parent.sublayers.length; i < len; ++i) {
                    findChildren(parent.sublayers[i]);
                }
            }
        };

        // enumerate through to handle the case where there are multiple roots
        for (let i = 0, len = roots.length; i < len; ++i) {
            findChildren(roots[i]);
        }

        return roots;
    }
    /**
     * Add custom groups
     */
    private addItemsToGroup(operationalItems: Collection<ListItem>): void {
        const {store} = this.props;
        const allItems = operationalItems.flatten((ite: __esri.ListItem) =>
            ite.children
        );
        // add groups (in umgekehrter Reihenfolge hinzufügen)
        this.itemsConfig.sort((a, b) => a.index - b.index).forEach((itemConfig: ListItemConfiguration) => {
            // check if layer has to be moved to a parent (group)
            if (itemConfig.hasOwnProperty("parent")) {
                const oi = allItems.find((item: __esri.ListItem) =>
                    item.layer.id === itemConfig.id
                );
                if (!oi) {
                    return null;
                }
                if (store?.LayerListStore.regroupedItems.has(oi.layer.id)) {
                    return oi;
                }

                this.addToParent(itemConfig, oi, operationalItems);

                // remember the regrouped item
                store?.LayerListStore.addRegroupedItem(oi.layer.id, oi);
            }
        });
        if(store?.appConfig.mode !== "production") {
            this.itemsConfig.forEach((itemConfig: ListItemConfiguration) => {
                if(!itemConfig.layer.id){
                    console.warn("Layer ID fehlt:");
                    console.warn(itemConfig.layer);
                }else if(!this.layerIds.includes(itemConfig.layer.id)
                    && !store?.mapStore.layers.has(itemConfig.layer.id)){
                    // Überprüfung via mapStore ist nicht zuverlässig,
                    // besser wäre via Konfiguration (MapViewerConfiguration.layers)
                    console.warn(`Die Layer ID "${itemConfig.layer.id}" wurde in der Karte nicht gefunden.`);
                }
            });
        }
        this.reorder();
        this.setOpenStatus();
        if(!store?.appStore.subtopic){
            this.setInitialVisibility();
        }
        const operationalItemsArray = operationalItems.flatten((ite: __esri.ListItem) =>
            ite.children
        ).toArray();
        store?.LayerListStore.setOperationalItemsArray(operationalItemsArray);
    }
    private setInitialVisibility(operationalItems?: Collection<ListItem>): void {
        if(typeof operationalItems === "undefined"){
            operationalItems = this.operationalItems;
        }
        operationalItems.forEach((item)=>{
            const layerUid = (item.layer as ExtendedLayer).uid;
            if(this.itemVisibility.has(layerUid)){
                item.visible = !!this.itemVisibility.get(layerUid);
                item.layer.visible = item.visible;
                if(item.visible && item.children.length>0){
                    this.setInitialVisibility(item.children);
                }
            }
        });
    }

    /**
     * Add list item to a parent list item
     * Also moves referenced layer to the layers list of the layers list of parent list item layer
     *
     * @param config
     * @param item
     * @param operationalItems
     */
    private addToParent(config: ListItemConfiguration, item: ListItem, operationalItems: Collection<ListItem>): void {
        const {store} = this.props;
        if (config.hasOwnProperty("parent")) {
            let parent;
            if (config.parent.hasOwnProperty("layer")) {
                const parentId = store?.uiStore.sublayerMap.get(config.parent.layer.id as string);
                parent = operationalItems.flatten((ite: __esri.ListItem) =>
                    ite.children
                ).find((listItem: ListItem) =>
                    listItem.layer?.id === config.parent.layer.id
                    || (listItem.layer as ExtendedLayer)?.uid === parentId);
                if (!parent) {
                    console.error(`No list item found by layer ID: ${config.parent.layer.id}`);
                    return;
                }
            } else if (config.parent.hasOwnProperty("title")) {
                parent = operationalItems.find((listItem: ListItem) =>
                    listItem.title === config.parent.title);
                if (!parent) {
                    console.error(`No list item found by title: ${config.parent.title}`);
                    return;
                }
            }
            if(!parent) {
                return;
            }

            item.parent = parent;
            if("limitLevel" in parent.layer){
                // limitLevel von parent Layer erben.
                const limitLevel: number = (parent.layer as ExtendedLayer).limitLevel;
                if (limitLevel > 1) {
                    (item.layer as ExtendedLayer).limitLevel = limitLevel - 1;
                }
            }
            if(parent.layer.hasOwnProperty("sublayers")){
                parent.children.add(item, config.index);
                // Layer ID ergänzen, damit dieser auch in der Suche verwendet werden kann
                (parent.layer as any).children.push(parseInt(item.layer.id, 10));
                const layerUid = (item.layer as ExtendedLayer).uid;
                let layerVisible=item.layer.visible;
                if(this.itemVisibility.has(layerUid)){
                    layerVisible=this.itemVisibility.has(layerUid);
                }else{
                    this.itemVisibility.set(layerUid, item.layer.visible);
                }
                // alle parent Layer berücksichtigen
                if(item.layer.visible){
                    let p = parent;
                    while(p){
                        this.itemVisibility.set((p.layer as any).uid, layerVisible);
                        p = p.parent;
                    }
                }
                if(!parent.layer.visible) {
                    item.layer.visible = false;
                }
                operationalItems.getItemAt(0).children.remove(item);
            } else if (parent.layer.hasOwnProperty("layers")) {
                if (parent.layer.type !== "group") {
                    console.error("Referenced parent layer is not a group layer. Cannot add layer to this parent.");
                    return;
                }
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore --> group layer has property 'layers'
                if (!parent.layer.layers.includes(item.layer)) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore --> group layer has property 'layers'
                    parent.layer.add(item.layer);
                    parent.children.add(item);
                }
            }
            (item.layer as ExtendedLayer).index = config.index;
            if (config.hasOwnProperty("title")) {
                item.title = config.title;
                item.layer.title = config.title;
            }
        }
    }
    private reorder(operationalItems?: Collection<ListItem>): void {
        const {config, store} = this.props;
        if(operationalItems === undefined) {
            operationalItems = this.operationalItems;
        } else if((config.limitLevel === 0 ||
            (config.limitLevel - config.startLevel) > 1) && !store?.LayerListStore.hasGroups){
            store?.LayerListStore.setHasGroups(true);
        }
        operationalItems.sort((a, b) =>
            (a.layer as ExtendedLayer).index - (b.layer as ExtendedLayer).index);
        operationalItems.forEach((item: __esri.ListItem) => {
            if(item.children.length > 1) {
                this.reorder(item.children);
            }
        });
    }
    private hideLayerItem(hide: boolean, layerItem: __esri.ListItem) {
        layerItem.hidden = hide;
        layerItem.visible = !hide;
        layerItem.layer.visible = !hide;
        if (layerItem.children && layerItem.children.length > 0) {
            layerItem.children.forEach((c: __esri.ListItem) => {
                this.hideLayerItem(hide, c);
            });
        }
    }
    private reformatListItemWhenCreated(event: any): void {
        // The event object contains an item property.
        // It is a ListItem referencing the associated layer
        // and other properties. You can control the visibility of the
        // item, its title, and actions using this object.

        const {store, config} = this.props;
        const item = event.item;
        // Layer aus CompareWidget ignorieren
        if(item.layer.metauid === "[compare]" || item.layer.title === "[compare]"){
            return;
        }
        const url = PathUtils.getLayerPath(item.layer);
        let meta = "";
        if (typeof url === "string") {
            const metaId = PathUtils.getMetaId(url);
            const layerId = PathUtils.getLayerId(url);
            if (typeof layerId === "number"){
                meta = metaId + "-" + layerId;
            } else {
                meta = metaId;
            }
        }
        if(store?.appConfig.mode !== "production"){
            this.layerIds.push(meta);
            this.layerIds.push(item.layer.id);
        }
        // is there a configuration for this item?
        const itemConfig = this.itemsConfig.find((lic: ListItemConfiguration) =>
            lic.layer.id === item.layer.id || lic.layer.id === meta
        );
        if (!itemConfig) {
            if(config.rebuild && item.layer.type !== "map-image"){
                if(typeof item.layer.index === "undefined") {
                    item.layer.listMode = "hide";
                    // Erkennen von Mosaic Layer (haben jeweils drei sublayer)
                    // vor 4.28 war typeof item.parent.layer.type === "undefined"
                    if(item.parent?.layer?.type === "sublayer" && item.parent.layer.sublayers?.length === 3){
                        item.layer.visible = true;
                        item.parent.layer.visibilityMode = "inherited";
                    } else {
                        // Nicht konfigurierte Layer ausblenden
                        item.layer.visible = false;
                    }
                }
            }
            this.itemVisibility.set(item.layer.uid, item.layer.visible);
            // nothing to do for this item
            return;
        }
        itemConfig.id = item.layer.id; // Layer an Konfiguration zuweisen.
        // see if layer item should be hidden
        if (itemConfig.hasOwnProperty("layer")) {
            if (itemConfig.layer.hasOwnProperty("listMode")) {
                item.layer.listMode = itemConfig.layer.listMode;
                if (itemConfig.layer.listMode === "hide") {
                    // also hide layer in the map
                    item.layer.visible = false;
                }
                // TODO: überprüfen, ob alle sublayers sichtbar geschaltet werden sollen, oder ob settings aus
                //  webmap übernommen werden sollen
                // if (itemConfig.layer.listMode === "hide-children") {
                //     item.children.forEach((child: __esri.ListItem) => {
                //         child.layer.visible = true;
                //     });
                // }
            }
            const visible = (itemConfig.hasOwnProperty("visible") && itemConfig.visible);
            let layerVisible = !!(itemConfig.layer.hasOwnProperty("visible") && itemConfig.layer.visible);
            if (itemConfig.hasOwnProperty("visible")) {
                layerVisible = visible;
            }
            if(config.rebuild){
                this.itemVisibility.set(item.layer.uid, layerVisible);
                item.visible = false;
                item.layer.visible = false;
            } else {
                item.visible = visible;
                item.layer.visible = layerVisible;
            }
            if ("metauid" in itemConfig.layer) {
                item.metauid = itemConfig.layer.metauid;
                item.layer.metauid = itemConfig.layer.metauid;
            }
            if ("anchorField" in itemConfig.layer) {
                item.layer.anchorField = itemConfig.layer.anchorField;
            }
        }
        // check if layer has a title
        if (itemConfig.hasOwnProperty("title")) {
            item.title = itemConfig.title;
            item.layer.title = itemConfig.title;
        }
        if (itemConfig.hasOwnProperty("index")) {
            item.index = itemConfig.index;
            item.layer.index = itemConfig.index;
        }

        // TODO: add actions to layers (e.g. transparency slider)
    }
    private setOpenStatus(): void {
        const {config, store} = this.props;
        const allItems = this.operationalItems.flatten((ite: __esri.ListItem) =>
            ite.children
        );
        this.operationalItems.getItemAt(0).open = true;
        if(config.groups){
            config.groups.forEach((itemConfig: ListItemConfiguration) => {
                const groupId = store?.uiStore.sublayerMap.get(itemConfig.layer.id as string);
                const groupItem = allItems.find((item: __esri.ListItem) =>
                    (item.layer as ExtendedLayer).uid === groupId
                );
                if(groupItem && groupId) {
                    // Status bleibt aus unerklärlichen Gründen nicht erhalten und muss mehrfach gesetzt werden
                    groupItem.open = itemConfig.open;
                    if(!this.groupOpenStatusSet.includes(groupId)) {
                        this.groupOpenStatusSet.push(groupId);
                        if (itemConfig.layer.hasOwnProperty("visible")) {
                            groupItem.visible = itemConfig.layer.visible as boolean;
                        }
                    }
                }
            });
        }
    }
}

export const LayerList = withStore(LayerListBase);
